<script setup lang="ts">
import {
  LucideShieldCheck,
  LucideShieldX,
  LucideSearch,
  LucideExternalLink,
  LucideInfo,
  LucideCheckCircle2,
  LucideAlertCircle
} from 'lucide-vue-next'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'

const { t } = useI18n()
const { getToken } = useUser()

const inn = ref('')
const isLoading = ref(false)
const error = ref<string | null>(null)
const result = ref<{ is_banned: boolean; inn: string } | null>(null)

const isValidInn = computed(() => /^\d{14}$/.test(inn.value))

watch(inn, (val) => {
  const cleaned = val.replace(/\D/g, '').slice(0, 14)
  if (cleaned !== val) inn.value = cleaned
})

const checkStatus = async () => {
  if (!isValidInn.value) return

  isLoading.value = true
  error.value = null
  result.value = null

  try {
    const token = getToken()
    result.value = await useApiCall<{ is_banned: boolean; inn: string }>('/self-ban/status', {
      method: 'POST',
      body: { inn: inn.value },
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    })
  } catch {
    error.value = t('feature.credit_lock.check_error')
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <div class="space-y-8">
    <div>
      <h2 class="text-lg font-semibold text-gray-900">
        {{ $t('feature.credit_lock.heading') }}
      </h2>
      <p class="mt-1 text-base text-gray-500">
        {{ $t('feature.credit_lock.subheading') }}
      </p>
    </div>

    <div class="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-5">
      <h3 class="text-base font-semibold text-gray-900">
        {{ $t('feature.credit_lock.what_is_heading') }}
      </h3>
      <p class="text-base leading-relaxed text-gray-600">
        {{ $t('feature.credit_lock.what_is_text') }}
      </p>
      <ul class="space-y-2">
        <li
          v-for="note in $tm('feature.credit_lock.important_notes')"
          :key="String(note)"
          class="flex items-start gap-2 text-base text-gray-600"
        >
          <LucideCheckCircle2 class="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
          <span>{{ $rt(note) }}</span>
        </li>
      </ul>
    </div>

    <div class="space-y-3">
      <label for="inn-input" class="block text-base font-semibold text-gray-700">
        {{ $t('feature.credit_lock.inn_label') }}
      </label>
      <div class="flex gap-3">
        <div class="relative flex-1">
          <Input
            id="inn-input"
            v-model="inn"
            inputmode="numeric"
            pattern="\d*"
            :maxlength="14"
            :placeholder="$t('feature.credit_lock.inn_placeholder')"
            :class="[
              'h-10 font-mono tracking-wider',
              inn.length > 0 && !isValidInn ? 'border-red-300 focus-visible:ring-red-400' : ''
            ]"
            @keyup.enter="checkStatus"
          />
          <span
            class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500"
          >
            {{ inn.length }}/14
          </span>
        </div>
        <Button :disabled="!isValidInn || isLoading" @click="checkStatus">
          <LucideSearch class="h-4 w-4" />
          {{ $t('feature.credit_lock.check_button') }}
        </Button>
      </div>
      <p v-if="inn.length > 0 && !isValidInn" class="text-xs text-red-500">
        {{ $t('feature.credit_lock.inn_validation') }}
      </p>
    </div>

    <div v-if="isLoading" class="flex items-center gap-3 text-base text-gray-500">
      <div
        class="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"
      />
      {{ $t('feature.credit_lock.loading') }}
    </div>

    <div
      v-if="error"
      class="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-base text-red-700"
    >
      <LucideAlertCircle class="mt-0.5 h-5 w-5 shrink-0" />
      {{ error }}
    </div>

    <div v-if="result && !isLoading">
      <div
        v-if="result.is_banned"
        class="flex gap-4 rounded-xl border border-red-200 bg-red-50 p-5"
      >
        <LucideShieldX class="mt-0.5 h-6 w-6 shrink-0 text-red-600" />
        <div>
          <p class="font-semibold text-red-800">{{ $t('feature.credit_lock.status_banned') }}</p>
          <p class="mt-1 text-base text-red-600">
            {{ $t('feature.credit_lock.status_banned_desc') }}
          </p>
        </div>
      </div>
      <div v-else class="flex gap-4 rounded-xl border border-green-200 bg-green-50 p-5">
        <LucideShieldCheck class="mt-0.5 h-6 w-6 shrink-0 text-green-600" />
        <div>
          <p class="font-semibold text-green-800">{{ $t('feature.credit_lock.status_clean') }}</p>
          <p class="mt-1 text-base text-green-600">
            {{ $t('feature.credit_lock.status_clean_desc') }}
          </p>
        </div>
      </div>
    </div>

    <div class="grid gap-4 sm:grid-cols-2">
      <div class="space-y-3 rounded-xl border border-gray-200 p-5">
        <h3 class="flex items-center gap-2 text-base font-semibold text-gray-900">
          <LucideShieldCheck class="h-4 w-4 text-green-600" />
          {{ $t('feature.credit_lock.how_to_enable_heading') }}
        </h3>
        <ol class="space-y-1.5">
          <li
            v-for="(step, i) in $tm('feature.credit_lock.enable_steps')"
            :key="i"
            class="flex items-start gap-2 text-base text-gray-600"
          >
            <span
              class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-100 text-xs font-semibold text-green-700"
            >
              {{ i + 1 }}
            </span>
            <span>{{ $rt(step) }}</span>
          </li>
        </ol>
      </div>

      <div class="space-y-3 rounded-xl border border-gray-200 p-5">
        <h3 class="flex items-center gap-2 text-base font-semibold text-gray-900">
          <LucideShieldX class="h-4 w-4 text-orange-500" />
          {{ $t('feature.credit_lock.how_to_disable_heading') }}
        </h3>
        <ol class="space-y-1.5">
          <li
            v-for="(step, i) in $tm('feature.credit_lock.disable_steps')"
            :key="i"
            class="flex items-start gap-2 text-base text-gray-600"
          >
            <span
              class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-orange-100 text-xs font-semibold text-orange-700"
            >
              {{ i + 1 }}
            </span>
            <span>{{ $rt(step) }}</span>
          </li>
        </ol>
        <p class="text-xs text-gray-500">
          {{ $t('feature.credit_lock.cooling_period_note') }}
        </p>
      </div>
    </div>

    <div class="space-y-2 rounded-xl border border-blue-100 bg-blue-50 p-5">
      <div class="flex items-center justify-between">
        <h3 class="text-base font-semibold text-blue-900">
          {{ $t('feature.credit_lock.about_sns_heading') }}
        </h3>
        <a
          href="https://sns.kg/"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 text-xs font-medium text-blue-700 underline underline-offset-2"
        >
          sns.kg
          <LucideExternalLink class="h-3 w-3" />
        </a>
      </div>
      <p class="text-base leading-relaxed text-blue-800">
        {{ $t('feature.credit_lock.about_sns_text') }}
      </p>
      <div class="flex items-start gap-2 pt-1">
        <LucideInfo class="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
        <p class="text-xs text-blue-700">
          {{ $t('feature.credit_lock.price_note') }}
        </p>
      </div>
    </div>
  </div>
</template>
